// AM Management — the permanent screen for viewing/editing any customer's
// Account Manager. Admin-only. Uses the dry-run/apply safety pattern: changing
// an AM first previews the before/after diff, then commits on confirm.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Layout from "../../components/Layout";
import { toast } from "sonner";
import { UserCog, Search, Loader2, ShieldCheck, ChevronLeft, ChevronRight, X } from "lucide-react";
import AMSmartTabs, { AM_TABS } from "./am-smart/AMSmartTabs";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AMManagement() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [amFilter, setAmFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [editRow, setEditRow] = useState(null);   // person row being edited
  const [pickUser, setPickUser] = useState("");
  const [preview, setPreview] = useState(null);    // dry-run diff
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkUser, setBulkUser] = useState("");
  const [tab, setTab] = useState("customers");
  const [denied, setDenied] = useState(false);
  const [ready, setReady] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (q.trim()) params.set("q", q.trim());
      if (amFilter.trim()) params.set("am", amFilter.trim());
      const { data } = await axios.get(`${API}/admin/am-management/persons?${params}`, { withCredentials: true });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Failed to load"));
    } finally { setLoading(false); }
  }, [page, q, amFilter, ready]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/admin/am-management/users`, { withCredentials: true })
      .then(({ data }) => { setUsers(data.items || []); setReady(true); }).catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  }, []);

  const openEdit = (row) => { setEditRow(row); setPickUser(""); setPreview(null); };

  const runPreview = async (userId) => {
    setPickUser(userId);
    setPreview(null);
    if (!userId) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/am-management/persons/${editRow.id}/set-am`,
        { userId, dry_run: true }, { withCredentials: true });
      setPreview(data);
    } catch (e) { toast.error(String(e?.response?.data?.detail || "Preview failed")); }
  };

  const applyChange = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/am-management/persons/${editRow.id}/set-am`,
        { userId: pickUser, dry_run: false }, { withCredentials: true });
      toast.success(`AM updated → ${preview?.after?.account_manager}`);
      setEditRow(null); setPreview(null); setPickUser("");
      load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || "Update failed")); }
    finally { setBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const unassignOne = async (r) => {
    if (!window.confirm(`Remove ${r.account_manager} as AM for ${r.name}?`)) return;
    try {
      await axios.post(`${API}/admin/am-management/bulk-set-am`, { person_ids: [r.id], clear: true }, { withCredentials: true });
      toast.success(`${r.name} unassigned`); setEditRow(null); load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || "Unassign failed")); }
  };
  useEffect(() => {
    if (!editRow) return undefined;
    const onKey = (e) => e.key === "Escape" && setEditRow(null);
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [editRow]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const allOnPage = rows.every((r) => s.has(r.id));
    const n = new Set(s);
    rows.forEach((r) => allOnPage ? n.delete(r.id) : n.add(r.id));
    return n;
  });
  const runBulk = async (clear) => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!clear && !bulkUser) { toast.error("Pick an account manager first"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/am-management/bulk-set-am`,
        clear ? { person_ids: ids, clear: true } : { person_ids: ids, userId: bulkUser },
        { withCredentials: true });
      toast.success(clear ? `Cleared AM on ${data.cleared} customer(s)` : `Assigned ${data.am} to ${data.assigned} customer(s)`);
      setSelected(new Set()); setBulkUser(""); load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || "Bulk update failed")); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-[1800px] mx-auto space-y-5" data-testid="am-management-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/15 border border-teal-500/30 grid place-items-center">
            <UserCog className="w-5 h-5 text-teal-300" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Account Manager Management</h1>
            <p className="text-[12px] text-slate-400">Assign, unassign and balance Account Managers across customers and tiers. Bulk changes ask before they save and can be undone.</p>
          </div>
        </div>

        {ready && <div className="flex flex-wrap gap-1 border-b border-white/10" data-testid="am-mgmt-tabs">
          {[...AM_TABS, ["classic", "Classic assign"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`am-mgmt-tab-${k}`}
              className={`px-3 py-2 text-[12.5px] -mb-px border-b-2 transition-colors ${tab === k ? "border-teal-400 text-white font-semibold" : "border-transparent text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>}
        {!ready && !denied && <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
        {denied && <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-4 text-[13px] text-rose-200" data-testid="am-mgmt-denied">You don't have AM Management access. Ask an admin to grant "Manage customer Account Managers".</div>}
        {ready && tab !== "classic" && <AMSmartTabs tab={tab} users={users} />}

        <div className={tab === "classic" && ready ? "space-y-5" : "hidden"}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Search customer name, email or company…"
              className="w-full pl-9 pr-3 h-10 rounded-lg bg-slate-900/70 border border-white/10 text-[13px] text-white outline-none focus:border-teal-400/60"
              data-testid="am-mgmt-search" />
          </div>
          <select value={amFilter} onChange={(e) => { setPage(1); setAmFilter(e.target.value); }}
            className="h-10 px-3 rounded-lg bg-slate-900/70 border border-white/10 text-[13px] text-white outline-none focus:border-teal-400/60"
            data-testid="am-mgmt-am-filter">
            <option value="">All account managers</option>
            {users.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
          </select>
          <a href={`${API}/crm/export/customers.csv?level=basic`}
            className="h-10 px-3 grid place-items-center rounded-lg border border-white/10 text-[12px] text-slate-200 hover:bg-white/5"
            data-testid="am-mgmt-export-basic">Export all · Basic CSV</a>
          <a href={`${API}/crm/export/customers.csv?level=advanced`}
            className="h-10 px-3 grid place-items-center rounded-lg border border-white/10 text-[12px] text-slate-200 hover:bg-white/5"
            data-testid="am-mgmt-export-advanced">Export all · Advanced ZIP</a>
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-teal-500/30 bg-teal-500/[0.06] px-3 py-2" data-testid="am-mgmt-bulk-bar">
            <span className="text-[12px] text-teal-200 font-mono">{selected.size} selected</span>
            <select value={bulkUser} onChange={(e) => setBulkUser(e.target.value)} data-testid="am-mgmt-bulk-user"
              className="h-9 px-2 rounded-lg bg-black/30 border border-white/10 text-[12px] text-white outline-none focus:border-teal-400/60">
              <option value="">Assign to…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <button onClick={() => runBulk(false)} disabled={busy || !bulkUser} data-testid="am-mgmt-bulk-assign"
              className="h-9 px-3 rounded-lg text-[12px] font-semibold border border-teal-500/40 bg-teal-500/15 text-teal-200 hover:bg-teal-500/25 disabled:opacity-40">Assign</button>
            <button onClick={() => runBulk(true)} disabled={busy} data-testid="am-mgmt-bulk-clear"
              className="h-9 px-3 rounded-lg text-[12px] font-semibold border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 disabled:opacity-40">Clear AM</button>
            <button onClick={() => setSelected(new Set())} className="h-9 px-3 rounded-lg text-[12px] text-slate-400 hover:bg-white/5">Cancel</button>
          </div>
        )}

        <div className="rounded-xl border border-white/10 overflow-x-auto">
          <table className="w-full min-w-[860px] text-[13px]">
            <thead className="bg-white/[0.02]"><tr className="text-[10px] font-mono uppercase tracking-widest text-slate-500 text-left">
              <th className="w-10 px-4 py-2.5"><input type="checkbox" checked={rows.length > 0 && rows.every((r) => selected.has(r.id))} onChange={toggleAll} data-testid="am-mgmt-select-all" /></th>
              <th className="px-2 py-2.5">Customer</th><th className="px-2 py-2.5">Email</th><th className="px-2 py-2.5">Account Manager</th><th className="px-2 py-2.5">Source</th><th className="px-4 py-2.5 text-right">Action</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="p-10 text-center text-slate-500">No customers match.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className={`border-t border-white/[0.04] hover:bg-white/[0.03] ${selected.has(r.id) ? "bg-teal-500/[0.04]" : ""}`} data-testid={`am-mgmt-row-${r.id}`}>
                  <td className="px-4 py-2.5"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`am-mgmt-check-${r.id}`} /></td>
                  <td className="px-2 py-2.5 max-w-[260px]"><div className="text-white truncate">{r.name}</div>{r.company && <div className="text-slate-500 text-[11px] truncate">{r.company}</div>}</td>
                  <td className="px-2 py-2.5 text-slate-400 text-[11.5px] font-mono max-w-[240px] truncate">{r.email || "—"}</td>
                  <td className="px-2 py-2.5" data-testid={`am-mgmt-current-${r.id}`}>
                    {r.account_manager
                      ? <span className="inline-flex items-center gap-1 text-teal-200">{r.account_manager}{r.am_confirmed && <ShieldCheck className="w-3 h-3 text-emerald-400" />}</span>
                      : <span className="text-amber-300/80">unassigned</span>}
                  </td>
                  <td className="px-2 py-2.5 text-[10px] font-mono text-slate-500">{r.am_source || "—"}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(r)} data-testid={`am-mgmt-edit-${r.id}`}
                      className="text-[11px] font-mono px-2.5 py-1 rounded-md border border-teal-500/40 bg-teal-500/10 text-teal-200 hover:bg-teal-500/20">Change</button>
                    {r.account_manager && <button onClick={() => unassignOne(r)} data-testid={`am-mgmt-unassign-${r.id}`}
                      className="ml-1.5 text-[11px] font-mono px-2.5 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20">Unassign</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-[12px] text-slate-400">
          <span data-testid="am-mgmt-total">{total.toLocaleString()} customers</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="am-mgmt-prev"
              className="px-2 py-1 rounded border border-white/10 disabled:opacity-40 hover:bg-white/5"><ChevronLeft className="w-4 h-4" /></button>
            <span>Page {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="am-mgmt-next"
              className="px-2 py-1 rounded border border-white/10 disabled:opacity-40 hover:bg-white/5"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        </div>
      </div>

      {editRow && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60" onClick={() => setEditRow(null)}
          data-testid="am-mgmt-modal">
          <div className="w-[460px] max-w-[92vw] rounded-xl border border-teal-500/25 bg-[#0d1420] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-mono uppercase tracking-widest text-teal-300 font-bold">Change Account Manager</span>
              <button onClick={() => setEditRow(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-[13px] text-white mb-1">{editRow.name}</div>
            <div className="text-[11px] text-slate-500 mb-4">
              Current: {editRow.account_manager || "unassigned"} {editRow.am_source ? `· ${editRow.am_source}` : ""}
            </div>
            <label className="text-[11px] text-slate-400 mb-1.5 block">New account manager</label>
            <select value={pickUser} onChange={(e) => runPreview(e.target.value)} data-testid="am-mgmt-user-select"
              className="w-full h-10 px-3 rounded-lg bg-black/30 border border-white/10 text-[13px] text-white outline-none focus:border-teal-400/60">
              <option value="">Select…</option>
              {users.filter((u) => u.name !== editRow.account_manager).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>

            {preview && (
              <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[12px]" data-testid="am-mgmt-preview">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Review change</div>
                <div className="flex items-center gap-2 text-slate-300">
                  <span className="text-slate-500">{preview.before.account_manager || "unassigned"}</span>
                  <span className="text-slate-600">→</span>
                  <span className="text-teal-200 font-semibold">{preview.after.account_manager}</span>
                  <span className="ml-auto inline-flex items-center gap-1 text-emerald-400 text-[10px]"><ShieldCheck className="w-3 h-3" /> confirmed</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-4">
              {editRow.account_manager && <button onClick={() => unassignOne(editRow)} data-testid="am-mgmt-modal-unassign"
                className="mr-auto px-3 py-1.5 rounded-lg text-[12px] border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20">Unassign</button>}
              <button onClick={() => setEditRow(null)} className="px-3 py-1.5 rounded-lg text-[12px] text-slate-300 hover:bg-white/5 border border-white/10">Cancel</button>
              <button onClick={applyChange} disabled={!pickUser || !preview || busy} data-testid="am-mgmt-apply"
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold border border-teal-500/40 bg-teal-500/15 text-teal-200 hover:bg-teal-500/25 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save change
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
